import React, { useMemo } from "react";
import { SurveySegment } from "../../../../amorg-common";
import { Flex } from "../../../../designSystem/compositeComponents/FlexHelpers";
import { Button } from "../../../../designSystem/button/Button";
import { SegmentListChipsView } from "./SegmentListChipsView";
import { SegmentListTableView } from "./SegmentListTableView";
import { SegmentationViewMode, amorgLocalStorage } from "../../../../utils/amorgLocalStorage";
import { Card } from "../../../../designSystem/Card";
import { OnOff } from "../../../../designSystem/inputs/OnOff";
import { TextInput } from "../../../../designSystem/inputs/TextInput";

export type SegmentListViewData = {
    segments: SurveySegment[],
    identifier: string,
    selected: boolean,
    answerCount: number;
};

export type BaseSegmentListViewProps = {
    segmentLists: SegmentListViewData[],
    displayAnswerCount?: boolean,
    noSelectionContent?: React.ReactNode,
    localStorageViewModeKey?: string,
    onToggleSelect?: (identifier: string, selected: boolean) => void,
    onToggleAll?(): void
};

export type SegmentListViewProps =
    BaseSegmentListViewProps &
    {
        lockedView?: SegmentationViewMode,
        wrapInCard?: boolean;
    };

export function SegmentListView (props: SegmentListViewProps): JSX.Element 
{
    return props.wrapInCard
        ?
        <Card variant="outlined">
            <InternalSegmentListView {...props} />
        </Card>
        :
        <InternalSegmentListView {...props} />;
}

export function InternalSegmentListView (props: SegmentListViewProps): JSX.Element 
{
    const [view, setView] = React.useState<SegmentationViewMode>('pills');
    const [inEditMode, setInEditMode] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');

    React.useEffect(() =>
    {
        if (props.lockedView)
        {
            setView(props.lockedView);
        }
        else if(props.localStorageViewModeKey)
        {
            const storedView = amorgLocalStorage.surveySegmentation.viewMode.readOrCreate(props.localStorageViewModeKey);
            setView(storedView);
        }
        // use default

    }, [props.lockedView]);

    function handleViewModeChange (e: React.MouseEvent<HTMLButtonElement>, mode: SegmentationViewMode)
    {
        setView(mode);
        if(props.localStorageViewModeKey)
            amorgLocalStorage.surveySegmentation.viewMode.write(props.localStorageViewModeKey, mode);
        e.stopPropagation();
    }

    function handleClickToggleAll ()
    {
        if (props.onToggleAll)
            props.onToggleAll();
    }

    function handleSearchTextChange (text: string)
    {
        setSearchText(text);
    }

    const shownSegmentLists = useMemo(() =>
    {
        let filtered = props.segmentLists;
        const showAll = props.onToggleSelect && inEditMode;
        if (!showAll)
        {
            filtered = props.segmentLists.filter(sl => sl.selected);
        }

        if (searchText.trim().length > 0)
        {
            return filtered.filter(sl =>
                sl.segments.some(s =>
                    s.name.toLowerCase().includes(searchText.toLowerCase())
                    || s.value.toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }

        return filtered;
    }, [props.segmentLists, props.onToggleSelect, inEditMode, searchText]);

    return (
        <>
            {props.segmentLists.length > 0 &&
                <Flex flexDirection="column" gap={16}>
                    <Flex justifyContent="space-between" alignItems="center" >
                        {props.onToggleSelect &&
                            <Flex justifyContent="flex-start" style={{ flexGrow: 1 }}>
                                <span style={{ fontSize: '14px' }}>{props.segmentLists.filter(s => s.selected).length} af {props.segmentLists.length} segmentlister valgt</span>
                            </Flex>
                        }
                        <Flex justifyContent="flex-end" alignItems="center" gap={8} style={{ flexGrow: 1 }}>
                            {props.onToggleSelect &&
                                <Button variant={'text'} icon={inEditMode ? 'actions_close' : 'interface_actions_edit'} toolTipText="Rediger valgte segmenter" onClick={() => setInEditMode(!inEditMode)} />
                            }
                            {props.lockedView === undefined &&
                                <>
                                    <Button variant={view === 'pills' ? 'text' : 'clean'} icon='toggle_on' toolTipText="Vis som piller" onClick={(e) => handleViewModeChange(e, 'pills')} />
                                    <Button variant={view === 'table' ? 'text' : 'clean'} icon='table' toolTipText="Vis som tabel" onClick={(e) => handleViewModeChange(e, 'table')} />
                                </>
                            }
                        </Flex>
                    </Flex>

                    {props.onToggleSelect && props.onToggleAll && inEditMode &&
                        <Flex justifyContent="space-between" style={{ height: '26px' }}>
                            <Flex alignItems="center" gap={4}>
                                <Flex alignItems="center" gap={4}>
                                    <span style={{ fontSize: '14px' }}>Vælg alle</span>
                                    <OnOff size="small" active={props.segmentLists.every(s => s.selected)} onClick={handleClickToggleAll} />
                                </Flex>

                            </Flex>
                            <TextInput placeholder="Søg..."
                                value={searchText}
                                onChange={handleSearchTextChange}
                                trailingIcon="interface_actions_search"
                            />
                        </Flex>
                    }

                    {shownSegmentLists.length > 0 && view === 'pills' &&
                        <SegmentListChipsView segmentLists={shownSegmentLists}
                            displayAnswerCount={props.displayAnswerCount}
                            onToggleSelect={inEditMode ? props.onToggleSelect : undefined}
                            onToggleAll={inEditMode ? props.onToggleAll : undefined} />
                    }

                    {shownSegmentLists.length > 0 && view === 'table' &&
                        <SegmentListTableView segmentLists={shownSegmentLists}
                            displayAnswerCount={props.displayAnswerCount}
                            onToggleSelect={inEditMode ? props.onToggleSelect : undefined}
                            onToggleAll={inEditMode ? props.onToggleAll : undefined} />
                    }

                    {shownSegmentLists.length === 0 &&
                        <Flex style={{ fontSize: '16px', paddingBottom: '24px' }} justifyContent="center">
                            {props.noSelectionContent || <i>Ingen segmentlister at vise</i>}
                        </Flex>
                    }

                </Flex>
            }
        </>
    );
}